import React, { useState, useEffect } from 'react';
import {
  Box,
  ThemeProvider,
  Paper,
  Typography,
  TextField,
  Button,
  Link,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, Email } from '@mui/icons-material';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import { Navigate, useNavigate } from 'react-router-dom';
import { loginWithAuth0, loginWithGoogle } from '../../services/apiService';
import { theme } from '../../style/theme';
import loginbackground from '../../Images/loginbackground.png';
import activeflow from '../../Images/active.png';
import failed from '../../Images/failed.png';
import executed from '../../Images/executed.png';
import AnimatedOverlayShowcase from '../../components/AnimatedOverlayShowcase';

export function LoginPage() {
  const navigate = useNavigate();
  const authToken = Cookies.get('authToken');
  const requiresPasswordChange =
    Cookies.get('requiresPasswordChange') === 'true';

  if (authToken) {
    if (requiresPasswordChange) {
      return <Navigate to="/change-password" replace />;
    }
    return <Navigate to="/userdashboard" replace />;
  }

  const [formData, setFormData] = useState({ username: '', password: '' });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      const leftPanel = document.getElementById('login-left-panel');
      if (leftPanel) {
        const rect = leftPanel.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setMousePosition({
          x: Math.max(0, Math.min(100, x)),
          y: Math.max(0, Math.min(100, y)),
        });
      }
    };

    const leftPanel = document.getElementById('login-left-panel');
    if (leftPanel) {
      leftPanel.addEventListener('mousemove', handleMouseMove);
      return () => leftPanel.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  const handlePasswordVisibility = () => setShowPassword((prev) => !prev);

  // Stronger field validators (mirroring Signup rules)
  const validateEmail = (email) => {
    if (!email) return 'Email is required';
    // if (!/\S+@\S+\.\S+/.test(email)) return 'Invalid email format';
    return '';
  };

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    // if (password.length < 6) return 'Password must be at least 6 characters';
    // if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password))
    //   return 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    return '';
  };

  const validateLogin = () => {
    const newErrors = {};
    const emailError = validateEmail(formData.username);
    const passwordError = validatePassword(formData.password);
    if (emailError) newErrors.username = emailError;
    if (passwordError) newErrors.password = passwordError;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    let error = '';
    if (name === 'username') {
      error = validateEmail(value);
    } else if (name === 'password') {
      error = validatePassword(value);
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = '';
    if (name === 'username') {
      error = validateEmail(value.trim());
    } else if (name === 'password') {
      error = validatePassword(value);
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    if (!validateLogin()) return;
    try {
      const { token, user, backendUser } = await loginWithAuth0(
        formData.username,
        formData.password,
      );
      Cookies.set('authToken', token, { expires: 1 });
      Cookies.set('userId', backendUser.user_id, { expires: 1, secure: true });
      Cookies.set('Org_id', backendUser.primary_org_id, {
        expires: 1,
        secure: true,
      });
      Cookies.set('Role_id', backendUser.role_id, { expires: 1, secure: true });
      Cookies.set(
        'username',
        user.username || user.name || user.nickname || user.email || 'Guest',
        { expires: 1, secure: true },
      );

      const needsPasswordChange =
        backendUser?.requires_password_change ||
        backendUser?.must_change_password;
      Cookies.set(
        'requiresPasswordChange',
        needsPasswordChange ? 'true' : 'false',
        { expires: 1, secure: true },
      );

      toast.success('Login successful!', { autoClose: 2000 });

      if (needsPasswordChange) {
        setTimeout(() => navigate('/change-password'), 2000);
      } else {
        setTimeout(() => navigate('/userdashboard'), 2000);
      }
    } catch (error) {
      toast.error(
        'Invalid login: ' +
          (error.response?.data?.error_description || 'Unknown error'),
        { autoClose: 2000 },
      );
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const { token, user, backendUser } = await loginWithGoogle();
      Cookies.set('authToken', token, { expires: 1, secure: true });
      Cookies.set('userId', backendUser.user_id, { expires: 1, secure: true });
      Cookies.set('Org_id', backendUser.primary_org_id, {
        expires: 1,
        secure: true,
      });
      Cookies.set('Role_id', backendUser.role_id, { expires: 1, secure: true });
      Cookies.set(
        'username',
        user.username || user.name || user.nickname || user.email || 'Guest',
        { expires: 1, secure: true },
      );

      const needsPasswordChange = backendUser?.requires_password_change || backendUser?.must_change_password;
      Cookies.set('requiresPasswordChange', needsPasswordChange ? 'true' : 'false', { expires: 1, secure: true });


      toast.success('Login successful!', { autoClose: 2000 });
      if (needsPasswordChange) {
        setTimeout(() => navigate('/change-password'), 2000);
      } else {
        setTimeout(() => navigate('/userdashboard'), 2000);
      }
    } catch (error) {
      toast.error(
        'Google Login failed: ' +
          (error.response?.data?.message || error.message),
      );
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          height: '100vh',
          width: '100%',
          display: 'flex',
          backgroundColor: '#f5f5f5',
        }}
      >
        <Box
          id="login-left-panel"
          sx={{
            flex: 1,
            background: '#3044A9',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'none',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              backgroundImage: `
                linear-gradient(rgba(255, 255, 255, 0.15) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255, 255, 255, 0.15) 1px, transparent 1px)
              `,
              backgroundSize: '80px 80px',
              opacity: 0.25,
            }}
          />

          {/* Cursor Following Grid Highlight */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              backgroundImage: `
                linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px)
              `,
              backgroundSize: '80px 80px',
              maskImage: `radial-gradient(350px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
              WebkitMaskImage: `radial-gradient(350px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
              transition:
                'mask-image 0.1s ease-out, -webkit-mask-image 0.1s ease-out',
            }}
          />

          {/* Fine Grid Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              backgroundImage: `
                linear-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px)
              `,
              backgroundSize: '20px 20px',
              opacity: 0.2,
            }}
          />

          {/* Cursor Following Fine Grid */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              backgroundImage: `
                linear-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255, 255, 255, 0.4) 1px, transparent 1px)
              `,
              backgroundSize: '20px 20px',
              maskImage: `radial-gradient(200px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
              WebkitMaskImage: `radial-gradient(200px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
              transition:
                'mask-image 0.1s ease-out, -webkit-mask-image 0.1s ease-out',
            }}
          />

          {/* Cursor Glow Effect */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 3,
              background: `radial-gradient(400px circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(255, 255, 255, 0.06) 0%, transparent 70%)`,
              transition: 'background 0.2s ease-out',
              pointerEvents: 'none',
            }}
          />

          {/* Animated Grid Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              backgroundImage: `
                radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.08) 0%, transparent 50%),
                radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.06) 0%, transparent 50%),
                radial-gradient(circle at 40% 80%, rgba(255, 255, 255, 0.04) 0%, transparent 50%)
              `,
              animation: 'gridPulse 8s ease-in-out infinite',
              '@keyframes gridPulse': {
                '0%, 100%': {
                  opacity: 0.3,
                },
                '50%': {
                  opacity: 0.6,
                },
              },
            }}
          />

          {/* Depth and Glow Effects */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              background: `
                radial-gradient(ellipse at center, rgba(255, 255, 255, 0.02) 0%, transparent 70%),
                linear-gradient(45deg, rgba(255, 255, 255, 0.03) 0%, transparent 50%),
                linear-gradient(-45deg, rgba(255, 255, 255, 0.02) 0%, transparent 50%)
              `,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '10%',
                left: '10%',
                right: '10%',
                bottom: '10%',
                background:
                  'radial-gradient(ellipse at center, rgba(255, 255, 255, 0.05) 0%, transparent 60%)',
                borderRadius: '50%',
                filter: 'blur(40px)',
                animation: 'float 6s ease-in-out infinite',
              },
              '@keyframes float': {
                '0%, 100%': {
                  transform: 'translateY(0px) scale(1)',
                  opacity: 0.3,
                },
                '50%': {
                  transform: 'translateY(-10px) scale(1.05)',
                  opacity: 0.5,
                },
              },
            }}
          />

          {/* SkyShift Logo at Top Left */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 4,
              p: 4,
            }}
          >
            <Typography
              onClick={() => navigate('/')}
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { md: '2rem', lg: '3rem' },
                background: 'linear-gradient(45deg, #ffffff 30%, #e0e7ff 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textAlign: 'left',
                cursor: 'pointer',
                width: 'fit-content',
                '&:hover': {
                  opacity: 0.8,
                  transform: 'scale(1.02)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Home
            </Typography>
          </Box>

          {/* Content Container */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              zIndex: 1,
              maxWidth: '700px',
              px: 4,
              gap: 4,
            }}
          >
            {/* Text content at top */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { md: '2.5rem', lg: '3rem' },
                  background:
                    'linear-gradient(45deg, #ffffff 30%, #e0e7ff 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Welcome Back!
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 400,
                  opacity: 0.9,
                  lineHeight: 1.6,
                  fontSize: { md: '1.1rem', lg: '1.25rem' },
                }}
              >
                Stay in flow—projects, filters, and dashboards intact. SkyShift
                restores your last session with pipeline states, logs, and
                alerts ready to go.
              </Typography>
            </Box>

            {/* Animated showcase below text */}
            <AnimatedOverlayShowcase
              backgroundSrc={loginbackground}
              layers={[
                {
                  src: activeflow,
                  alt: 'Active Flow',
                  from: 'bottom',
                  style: {
                    top: '20%',
                    left: '-10%',
                    width: '20%',
                    transform: 'translateX(0)',
                  },
                },
                {
                  src: failed,
                  alt: 'Failed',
                  from: 'bottom',
                  style: {
                    bottom: '10%',
                    left: '-10%',
                    width: '20%',
                    transform: 'translateX(0)',
                  },
                },
                {
                  src: executed,
                  alt: 'Executed',
                  from: 'bottom',
                  style: {
                    top: '15%',
                    right: '-10%',
                    width: '20%',
                    transform: 'translateX(0)',
                  },
                },
              ]}
            />
          </Box>
        </Box>

        {/* Right Panel - Login Form */}
        <Box
          sx={{
            flex: { xs: 1, md: 0.6 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'white',
            p: { xs: 3, sm: 4, md: 6 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: '400px',
              p: { xs: 3, sm: 4 },
              backgroundColor: 'transparent',
            }}
          >
            {/* Login Form Content */}
            <Box sx={{ width: '100%' }}>
              <Typography
                variant="h4"
                fontWeight={600}
                gutterBottom
                sx={{
                  color: '#1f2937',
                  textAlign: 'center',
                  mb: { xs: 2, sm: 3, md: 4 },
                  fontSize: {
                    xs: '1.75rem',
                    sm: '2rem',
                    md: '2.25rem',
                  },
                }}
              >
                Login
              </Typography>

              <Box
                component="form"
                onSubmit={handleLogin}
                sx={{ mt: { xs: 0.5, sm: 1, md: 1.5 } }}
              >
                <TextField
                  fullWidth
                  name="username"
                  placeholder="Email"
                  value={formData.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.username}
                  helperText={errors.username}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email
                          sx={{
                            color: '#9ca3af',
                            fontSize: { xs: '1.2rem', sm: '1.4rem' },
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: { xs: 2, sm: 2.5 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#ffffff',
                      fontSize: { xs: '0.95rem', sm: '1rem' },
                      '& fieldset': {
                        borderColor: '#e5e7eb',
                        borderWidth: '1px',
                      },
                      '&:hover fieldset': {
                        borderColor: '#d1d5db',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#4F46E5',
                        borderWidth: '2px',
                      },
                    },
                    '& .MuiInputBase-input': {
                      height: '20px',
                      padding: '12px 14px',
                      color: '#374151',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#9ca3af',
                      opacity: 1,
                    },
                  }}
                />

                <TextField
                  fullWidth
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.password}
                  helperText={errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handlePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOff
                              sx={{
                                color: '#9ca3af',
                                fontSize: { xs: '1.2rem', sm: '1.4rem' },
                              }}
                            />
                          ) : (
                            <Visibility
                              sx={{
                                color: '#9ca3af',
                                fontSize: { xs: '1.2rem', sm: '1.4rem' },
                              }}
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: { xs: 1, sm: 1.5 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#ffffff',
                      fontSize: { xs: '0.95rem', sm: '1rem' },
                      '& fieldset': {
                        borderColor: '#e5e7eb',
                        borderWidth: '1px',
                      },
                      '&:hover fieldset': {
                        borderColor: '#d1d5db',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#4F46E5',
                        borderWidth: '2px',
                      },
                    },
                    '& .MuiInputBase-input': {
                      height: '20px',
                      padding: '12px 14px',
                      color: '#374151',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#9ca3af',
                      opacity: 1,
                    },
                  }}
                />

                <Box sx={{ textAlign: 'right', mb: 2 }}>
                  <Link
                    onClick={() => navigate('/forgotpwd')}
                    sx={{
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      color: '#4F46E5',
                      textDecoration: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Forgot Password?
                  </Link>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    height: '48px',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '1rem',
                    mb: 3,
                    background: '#4F46E5',
                    color: '#ffffff',
                    textTransform: 'none',
                    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
                    '&:hover': {
                      background: '#4338CA',
                      boxShadow: '0 6px 16px rgba(79, 70, 229, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  LOGIN
                </Button>

                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6b7280',
                      fontSize: '0.875rem',
                    }}
                  >
                    Need an account?{' '}
                    <Link
                      onClick={() => navigate('/signup')}
                      sx={{
                        cursor: 'pointer',
                        color: '#4F46E5',
                        textDecoration: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Signup
                    </Link>
                  </Typography>
                </Box>

                <Divider
                  sx={{
                    mb: 3,
                    color: '#6b7280',
                    fontSize: '0.875rem',
                    '&::before, &::after': {
                      borderColor: '#e5e7eb',
                    },
                  }}
                >
                  OR
                </Divider>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleGoogleLogin}
                  sx={{
                    height: '48px',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    color: '#374151',
                    borderColor: '#e5e7eb',
                    backgroundColor: '#ffffff',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#f9fafb',
                      borderColor: '#d1d5db',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 28 28"
                    fill="none"
                    style={{ marginRight: 8 }}
                  >
                    <path
                      d="M27.5007 14.0819C27.5007 12.9493 27.4088 12.1227 27.2099 11.2656H14.2764V16.3777H21.868C21.715 17.6482 20.8885 19.5614 19.0518 20.847L19.026 21.0182L23.1154 24.1861L23.3987 24.2144C26.0006 21.8114 27.5007 18.2757 27.5007 14.0819Z"
                      fill="#4285F4"
                    />
                    <path
                      d="M14.2764 27.5508C17.9957 27.5508 21.1181 26.3263 23.3987 24.2142L19.0518 20.8468C17.8886 21.658 16.3274 22.2243 14.2764 22.2243C10.6337 22.2243 7.54189 19.8214 6.43976 16.5L6.27821 16.5137L2.02607 19.8045L1.97046 19.9591C4.23571 24.459 8.88871 27.5508 14.2764 27.5508Z"
                      fill="#34A853"
                    />
                    <path
                      d="M6.43936 16.5005C6.14855 15.6434 5.98025 14.725 5.98025 13.776C5.98025 12.827 6.14855 11.9087 6.42406 11.0516L6.41636 10.869L2.11093 7.52539L1.97006 7.59239C1.03644 9.45974 0.500732 11.5567 0.500732 13.776C0.500732 15.9954 1.03644 18.0922 1.97006 19.9596L6.43936 16.5005Z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M14.2764 5.32642C16.8631 5.32642 18.6079 6.44374 19.6029 7.37746L23.4905 3.58158C21.1029 1.36223 17.9957 0 14.2764 0C8.88871 0 4.23571 3.09177 1.97046 7.59167L6.42446 11.0508C7.54189 7.72947 10.6337 5.32642 14.2764 5.32642Z"
                      fill="#EB4335"
                    />
                  </svg>
                  Continue with Google
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default LoginPage;
